import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog'; // kitlint-allow-material: OrgAddressDialog (shared, outside this folder) is still a legacy MatDialog
import { GrowlerService } from '../../../shared/growler/growler.service';
import { PayablesService } from '../../../../services/payables/payables.service';
import { Payable } from '../../../../models/entities/payable';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { ConfirmDialogComponent, ConfirmDialogRequest } from '../../../v2-layout/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { PayableAttachmentCarouselComponent } from '../payables-dashboard/payable-attachment-carousel/payable-attachment-carousel.component';
import { GenericDialogResult } from '../../shared/generic-mat-dialog/generic-dialog-result';
import { UploadedFile } from '../../../../models/entities/uploaded-file';
import { PayableDetailFormComponent } from '../payable-detail-form/payable-detail-form.component';
import { FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { catchError, distinctUntilChanged, exhaustMap, filter, finalize, map, switchMap, take, timeout } from 'rxjs/operators';
import { BankAccountsService } from '../../../../services/financial-accounts/bank-accounts.service';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { BankAccountInsightsService } from '../../../../services/financial-accounts/bank-account-insights.service';
import { LobBankAccount } from '../../../../models/entities/lob-bank-account';
import { AddBillPayAccountComponent, AddBillPayBankAccountOutput } from '../../bill-pay/add-bill-pay-account/add-bill-pay-account.component';
import { Transaction } from '../../../../models/entities/transactions/transaction';
import { Organization } from '../../../../models/entities/organization';
import { OrganizationService } from '../../../../services/organization/organization.service';
import moment from 'moment-timezone';
import { EMPTY, merge, Observable, of, ReplaySubject, timer } from 'rxjs';
import { PayableActionType, PaymentMethod, PaymentRecordType, PaymentSchedule } from '../../../../enums/payables/payable-action.enum';
import { PayableDialogCaller } from '../../../../enums/payables/payable-dialog-caller.enum';
import { MarkAsPaidFormComponent } from '../mark-as-paid/mark-as-paid-form.component';
import { CheckFormatOptionsComponent } from '../check-printing/check-format-options/check-format-options.component';
import { PrintableCheckBatch } from '../../../../models/entities/printable-check-batch';
import { CheckPrintingService } from '../../../../services/payables/check-printing.service';
import { CsvExportService } from '../../../../services/csv-export/csv-export.service';
import { PayableActionsService } from '../../../../services/payables/payable-actions.service';
import { environment } from 'environments/environment';
import { PayabliService } from '../../../../services/payables/payabli.service';
import { EchoService } from '../../../../services/echo.service';
import { CheckPrintingSettingsService } from '../../../../services/payables/check-printing-settings.service';

import { VendorBankAccount } from '../../../../models/entities/vendors/vendor-bank-account';
import { SharedModule } from '../../../shared/shared.module';
import { UploadInvoiceFormComponent } from '../payables-dashboard/upload-invoice-form/upload-invoice-form.component';
import { CheckNumberInputComponent } from '../../../shared/check-number-input/check-number-input.component';
import { PayableReviewComponent } from '../payable-review/payable-review.component';
import { ControlMessagesComponent } from '../../../shared/control-messages/control-messages.component';
import { LobBankAccountSelectorComponent } from '../../accounting/lob-bank-account-selector/lob-bank-account-selector.component';
import { OrgAddressDialogComponent } from '../../shared/org-address-dialog/org-address-dialog.component';
import { Router } from '@angular/router';
import { awaitsSecondUnderwriting, TreasuryService, TreasuryUnderwritingStatus } from '../../../../services/stripe-treasury/treasury.service';
import { LegfiCurrencyPipe } from '../../../../pipes/format/legfi-currency.pipe';
import { ApprovalRequiredDialogComponent } from '../../../v2-layout/stripe-treasury/dialogs/approval-required-dialog/approval-required-dialog.component';

export interface CreatePayableDialogInput
{
    payable?: Payable;
    vendorId?: number;
    callerContext?: PayableDialogCaller;
}

export interface CreatePayableDialogResult
{
    success?: boolean;
}

interface PayableAction {
    value: PayableActionType;
    label: string;
}

interface RadioItem {
    value: string | number;
    label: string;
    disabled?: boolean;
}

/**
 * Shape of an async OCR result, whether it arrives via the live broadcast (`InvoiceOcrCompleted`)
 * or the polling fallback (`getOcrStatus`). Both sources carry the same fields; the
 * `pending` placeholder only sets `ocrStatus`. See SD-7269.
 */
interface OcrStatusResult {
    ocrStatus: string;
    uploadedFileId?: number;
    ocrAttempted?: boolean;
    ocrSuccess?: boolean;
    payable?: Payable;
}

/**
 * Multi-step dialog for creating payables with approval workflows and multiple payment methods.
 *
 * Handles distinct payment flows: mail check (Lob API), print check (batch creation), and mark as paid (transaction matching).
 * Print check requires creating a check batch BEFORE the payable due to PDF generation requirements.
 * Approval rules dynamically affect available actions - approved payables process automatically on scheduled date.
 */
@UntilDestroy()
@Component({
    selector: 'app-create-payable-dialog',
    templateUrl: './create-payable-dialog.component.html',
    styleUrls: ['./create-payable-dialog.component.scss'],
    standalone: true,
    imports: [
        ApprovalRequiredDialogComponent,
        SharedModule,
        PayableDetailFormComponent,
        UploadInvoiceFormComponent,
        PayableAttachmentCarouselComponent,
        CheckNumberInputComponent,
        PayableReviewComponent,
        ControlMessagesComponent,
        LobBankAccountSelectorComponent,
        CheckFormatOptionsComponent,
        MarkAsPaidFormComponent,
        AddBillPayAccountComponent,
        ConfirmDialogComponent,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CreatePayableDialogComponent implements OnInit
{
    @Input() open = false;
    @Input() data: CreatePayableDialogInput = {};
    @Output() readonly closed = new EventEmitter<CreatePayableDialogResult | null>();
    @Output() refreshPayablesEvent = new EventEmitter<Payable>();

    confirmRequest: ConfirmDialogRequest | null = null;

    readonly stepLabels = ['Invoice Details', 'Additional Details', 'Review'];
    step = 0;
    @ViewChild('payableDetailForm') payableDetailForm: PayableDetailFormComponent;
    @ViewChild('dialogContent') dialogContent: ElementRef;
    @ViewChild('payableCarousel') payableCarousel: PayableAttachmentCarouselComponent;
    @ViewChild('markAsPaidFormComponent', {static: false}) markAsPaidFormComponent: MarkAsPaidFormComponent;

    /**
     * OCR runs asynchronously on the server (SD-7269): the upload request returns immediately,
     * then the server broadcasts the result on the org's private payable-ocr channel. We react to
     * that live push, with a cached-status poll as a fallback for a missed broadcast (see
     * OCR_POLL_INITIAL_DELAY_MS). Multi-page PDFs go through AWS Textract's async path
     * and can take a while, so the safety cap is generous. If the cap is hit the `finalize()`
     * clears `isProcessing` (so the stepper never stays locked) and the user enters details
     * manually -- the upload itself is already saved either way.
     */
    private static readonly OCR_RESULT_TIMEOUT_MS = 120000;

    /**
     * The live broadcast is primary, but a single missed push (subscription race, websocket
     * reconnect, or a completion that lands a beat after we subscribe) must never leave the
     * stepper frozen until the safety cap. So we also poll the cached OCR status as a reliable
     * fallback: the first read fires shortly after we subscribe -- early enough to catch a fast
     * extraction that beat the Pusher handshake -- then repeats on an interval until a terminal
     * status arrives or OCR_RESULT_TIMEOUT_MS is hit. Whichever source (push or poll) yields a
     * non-pending result first wins. See SD-7269.
     */
    private static readonly OCR_POLL_INITIAL_DELAY_MS = 2500;
    private static readonly OCR_POLL_INTERVAL_MS = 3000;

    /**
     * Safety cap on how long an upload waits for the org profile to settle before deciding on OCR
     * (SD-7626). Angular's HttpClient applies no default timeout, so a stalled/black-holed profile GET
     * could otherwise leave the stepper locked forever. On expiry we fall back to manual entry, mirroring
     * the profile-load error path — preserving this dialog's "the stepper never freezes" guarantee.
     */
    private static readonly ORG_PROFILE_SETTLE_TIMEOUT_MS = 120000;

    /**
     * Shown when OCR is attempted but extraction fails, or when the OCR request errors/times out.
     * Kept as constants so the `next` (ocrSuccess=false) and `error` handlers stay in sync.
     */
    private static readonly OCR_FAILED_TITLE = 'Data Extraction Failed';
    private static readonly OCR_FAILED_BODY = 'Please enter invoice details manually.';

    // Standalone feature flag (per-org, default off) that enables invoice OCR on manual uploads
    // independently of Payabli bill-pay. Kept in sync with FeatureFlagServiceInterface::INVOICE_OCR
    // (core). See SD-7794.
    private static readonly INVOICE_OCR_FEATURE_FLAG = 'invoice-ocr.enabled';

    localPayable: Payable = null;
    isProcessing: boolean = false;
    addBankAccountOpen = false;
    // SD-7656: true only while invoice OCR is actually running (server-side AWS Textract can take
    // 25-30s). Drives the "Extracting invoice details…" overlay on the detail form so the dimmed
    // form never reads as frozen/blank during the wait. Distinct from isProcessing, which is also
    // true for uploads and step transitions and for non-OCR orgs that skip extraction entirely.
    isExtractingInvoice: boolean = false;
    payableActionFormGroup: UntypedFormGroup = this.initPayableActionForm();
    organizationId: number;
    organization: Organization;
    hasCompleteOrgAddress: boolean = false;

    /**
     * SD-9900 / SD-10162: the org's Treasury second-underwriting read. ACH from the Treasury balance
     * needs outbound transfers. Held back only by a review in progress, ACH stays selectable and
     * Continue answers with the Approval Required dialog; switched off for any other reason, ACH is
     * disabled. Null (not read, unavailable, or no Treasury account) gates nothing — the backend gate
     * remains authoritative.
     */
    underwritingStatus: TreasuryUnderwritingStatus | null = null;

    /** SD-10162: the Treasury available balance ACH pays from, in cents. Null until read. */
    treasuryAvailableCents: number | null = null;

    /** SD-10162: why ACH cannot pay this payable; shown under the payment methods and on Continue. */
    readonly achExceedsBalanceMessage = 'ACH transfer: amount exceeds available balance';

    /**
     * Whether the async org-profile load (getOrganizationProfile) has settled — success OR error.
     * OCR availability is gated on isInvoiceOcrAvailable, which reads the org profile
     * (hasPayabliIntegration); a drag-dropped invoice can finish uploading before that load resolves
     * (SD-7626), so the upload handler must know whether the gate is reliable yet or still pending.
     */
    private organizationProfileLoadSettled = false;

    /**
     * Whether the org-profile load ended in error (a failed GET or the fail-safe timeout below) rather
     * than a successful read. Distinct from a genuinely-incomplete address: on error we don't know the
     * address, so the template shows a "couldn't verify — retry" state instead of the misleading
     * "Organization address required" warning that would otherwise fire on the default-false gate
     * (SD-7438, AC #3).
     */
    organizationProfileLoadFailed = false;

    /**
     * Emits once the org-profile load settles (success or error). The upload handler waits on this
     * before deciding whether to dispatch OCR, so a fast drag-drop upload no longer loses the race
     * against the profile GET and silently skips OCR (SD-7626).
     */
    private readonly organizationLoaded$ = new ReplaySubject<void>(1);
    bankAccounts: LobBankAccount[] = [];
    potentialTransactionMatches: Transaction[] = [];
    invoiceUploadedFile: UploadedFile;
    minDate: Date = this._payablesService.minScheduledPaymentDate();
    isClosing = false;
    lobMailCheckFee: string;
    checkPrintingDefaults: any = {
        format: 'one-per-page',
        displayBankAccount: false,
        displayPayor: false,
        displayCheckNumber: false,
        displayVendorAddress: false
    };

    readonly PayableActionType = PayableActionType;
    readonly PaymentMethod = PaymentMethod;
    readonly PaymentSchedule = PaymentSchedule;
    readonly PaymentRecordType = PaymentRecordType;

    private readonly actions = {
        withRule: [
            { value: PayableActionType.SCHEDULE, label: 'Send for approval & schedule payment' },
            { value: PayableActionType.APPROVAL_ONLY, label: 'Send for approval only' },
        ],
        withoutRule: [
            { value: PayableActionType.SCHEDULE, label: 'Schedule payment' },
        ],
        common: [
            { value: PayableActionType.MARK_PAID, label: 'Mark as paid' },
            { value: PayableActionType.SAVE_DRAFT, label: 'Save as a draft and pay later' },
        ],
    };

    private _checkFormatOptions: CheckFormatOptionsComponent;
    private readonly _itemCache = new Map<string, {key: string, value: unknown}>();
    private readonly _currency = new LegfiCurrencyPipe();

    /** SD-10162: the Approval Required dialog, shown when a review still under way is all that stands in the way. */
    approvalRequiredOpen = false;

    constructor(
            private _dialog: MatDialog,
            private growlerService: GrowlerService,
            private _payablesService: PayablesService,
            private _organizationService: OrganizationService,
            private _bankAccountsService: BankAccountsService,
            private _checkPrintingService: CheckPrintingService,
            private _csvExportService: CsvExportService,
            private _payableActionsService: PayableActionsService,
            private _payabliService: PayabliService,
            private _checkPrintingSettingsService: CheckPrintingSettingsService,
            private _changeDetectorRef: ChangeDetectorRef,
            private _router: Router,
            private _echoService: EchoService,
            private _bankAccountInsights: BankAccountInsightsService,
            private _treasuryService: TreasuryService,
    ) {
        this.setupFormValidation();
        this.lobMailCheckFee = this._payablesService.getFormattedLobMailCheckFee();
    }

    ngOnInit(): void {
        this.loadDialogData();
        this.loadCheckPrintingDefaults();
        this.loadUnderwritingStatusWhenTreasuryAccountPresent();
    }

    private loadDialogData(): void {
        this.localPayable = this.data?.payable;
        // SD-7271/7407/7474: resolve the operating org from the payable itself. Edit / inbox /
        // from-attachment / move-money flows open this dialog for a payable whose org can differ
        // from the token org (multi-org / PMC users). The save scopes the chosen category to
        // this.organizationId, so the category picker must resolve the SAME org — otherwise the
        // picker offers a token-org category that fails findOrFail against the payable's org (404).
        // Fresh create has no payable yet, so fall back to the token org.
        this.organizationId = this.localPayable?.organizationId ?? LegFiJwtService.read()?.orgId;
        this.invoiceUploadedFile = this.localPayable?.attachments?.find(a => a.id === this.localPayable?.invoiceUploadedFileId);
        this.getBankAccounts();
        this.getOrganizationProfile();
    }

    onDialogClose(): void {
        if (this.addBankAccountOpen || this.confirmRequest) {
            return;
        }
        this.handleCloseAttempt();
    }

    onConfirmClosed(confirmed: boolean): void {
        const request = this.confirmRequest;
        this.confirmRequest = null;
        request?.onClose(confirmed);
    }

    @ViewChild('checkFormatOptions')
    set checkFormatOptions(value: CheckFormatOptionsComponent) {
        this._checkFormatOptions = value;
        if (value &&
                this.checkPrintingDefaults &&
                this.payableActionFormGroup.get('paymentMethod').value === PaymentMethod.PRINT_CHECK &&
                !value.formatForm.dirty) {
            setTimeout(() => {
                value.setFormatValues(this.checkPrintingDefaults);
                this._changeDetectorRef.detectChanges();
            }, 0);
        }
    }
    get checkFormatOptions(): CheckFormatOptionsComponent {
        return this._checkFormatOptions;
    }

    get invoiceAttachment(): UploadedFile {
        return this.invoiceUploadedFile;
    }

    get hasApprovalRule(): boolean {
        return !!this.localPayable?.currentFulfillment?.approvalRuleId;
    }

    get availableActions(): PayableAction[] {
        if (this.isZeroAmount) {
            // only show MARK_PAID and SAVE_DRAFT
            return this.actions.common;
        }

        return [
            ...(this.hasApprovalRule ? this.actions.withRule : this.actions.withoutRule),
            ...this.actions.common,
        ];
    }

    get isZeroAmount(): boolean {
        return this.localPayable?.amount === 0;
    }

    get invoiceActionItems(): RadioItem[] {
        const actions = this.availableActions;

        return this.memo('invoiceAction', actions.map(a => a.value).join('|'), () => actions.map(a => ({
            value: a.value,
            label: a.label,
        })));
    }

    /**
     * The disabled reasons that used to sit under each radio now sit under the group, because a
     * kit radio option is a label and nothing else. Anything that is only a qualifier goes into
     * the label itself; anything with a link stays a hint below.
     */
    get paymentMethodItems(): RadioItem[] {
        const zero = this.isZeroAmount ? ' (not available for $0)' : '';
        const key = [
            this.isZeroAmount, this.hasPayabliIntegration, this.hasTreasuryAccount, this.isVendorAchEnabled,
            this.achSwitchedOff, this.achExceedsBalance, this.treasuryAvailableCents,
            this.hasCompleteOrgAddress, this.verifiedBillPayAccounts.length, this.lobMailCheckFee,
        ].join('|');

        return this.memo('paymentMethod', key, () => {
            const items: RadioItem[] = [];

            if (this.hasPayabliIntegration) {
                items.push({
                    value: PaymentMethod.VENDOR_DECIDE,
                    label: `Let the vendor decide${zero}`,
                    disabled: this.isZeroAmount,
                });
            }

            if (this.hasTreasuryAccount) {
                // SD-10162: names the balance it pays from, once read.
                const balance = this.treasuryAvailableCents === null
                    ? ''
                    : ` of ${this._currency.transform(this.treasuryAvailableCents, 'USD', 'symbol', '.2')}`;
                items.push({
                    value: PaymentMethod.ACH_TRANSFER,
                    label: `ACH transfer, direct from your Treasury Balance${balance}${zero}`,
                    disabled: !this.isVendorAchEnabled || this.isZeroAmount || this.achSwitchedOff || this.achExceedsBalance,
                });
            }

            items.push({
                value: PaymentMethod.MAIL_CHECK,
                label: `Mail a check (${this.lobMailCheckFee} will be charged to your PayHOA account)${zero}`,
                disabled: this.verifiedBillPayAccounts.length === 0 || !this.hasCompleteOrgAddress || this.isZeroAmount,
            });

            items.push({
                value: PaymentMethod.PRINT_CHECK,
                label: `Print a check${zero}`,
                disabled: this.verifiedBillPayAccounts.length === 0 || this.isZeroAmount,
            });

            return items;
        });
    }

    get whenToPayItems(): RadioItem[] {
        const approvals = this.hasApprovalRule ? ' (after all approvals are granted)' : '';

        return this.memo('whenToPay', approvals, () => [
            {value: PaymentSchedule.IMMEDIATE, label: `Immediately${approvals}`},
            {value: PaymentSchedule.NO_EARLIER_THAN, label: `Schedule payment date${approvals}`},
        ]);
    }

    get vendorBankAccountItems(): RadioItem[] {
        const accounts = this.selectedVendorBankAccounts;

        return this.memo('vendorBankAccount', accounts.map(a => a.id).join('|'), () => accounts.map(account => ({
            value: account.id,
            label: `${account.accountHolderName} — ${this.titleCase(account.accountType)} • ${account.last4}`,
        })));
    }

    get minDay(): string {
        return moment(this.minDate).format('YYYY-MM-DD');
    }

    private titleCase(value: string): string {
        return (value ?? '').replace(/\w\S*/g, word => word[0].toUpperCase() + word.slice(1).toLowerCase());
    }

    /** Kit elements take arrays by identity, so a getter must hand back the same one until its inputs move. */
    private memo<T>(name: string, key: string, build: () => T): T {
        const hit = this._itemCache.get(name);
        if (hit && hit.key === key) {
            return hit.value as T;
        }

        const value = build();
        this._itemCache.set(name, {key, value});

        return value;
    }

    get confirmButtonLabel(): string {
        const action = this.payableActionFormGroup?.get('invoiceAction')?.value;
        const isPrintCheck = this.payableActionFormGroup?.get('paymentMethod')?.value === PaymentMethod.PRINT_CHECK;
        const isMatchingPayment = this.markAsPaidFormComponent?.getFormGroup().get('paymentRecordType')?.value === PaymentRecordType.MATCH;

        // Define button labels based on action type and conditions
        const buttonLabels = {
            [PayableActionType.MARK_PAID]: () =>
                    isMatchingPayment ? 'Match & Mark as Paid' : 'Create Payment Record',

            [PayableActionType.SCHEDULE]: () => {
                if (isPrintCheck) {
                    return this.hasApprovalRule ? 'Send for Approval' : 'Create Check & Mark Payment as Paid';
                }
                if (this.payableActionFormGroup?.get('paymentMethod')?.value === PaymentMethod.ACH_TRANSFER) {
                    if (this.hasApprovalRule) {
                        return 'Send for Approval';
                    }
                    // A future-dated ACH is held and scheduled, not initiated immediately.
                    return this.payableActionFormGroup?.get('whenToPay')?.value === PaymentSchedule.NO_EARLIER_THAN
                            ? 'Create Payable & Schedule ACH'
                            : 'Create Payable & Initiate ACH';
                }
                return this.hasApprovalRule ? 'Send for Approval & Schedule Payment' : 'Schedule Payment';
            },

            [PayableActionType.APPROVAL_ONLY]: () => 'Send for Approval',

            [PayableActionType.SAVE_DRAFT]: () => 'Save as Draft',
        };

        return buttonLabels[action]?.();
    }

    get hasPayabliIntegration(): boolean {
        // The org profile loads asynchronously and the load can fail (SD-7538). Until it resolves
        // we cannot know the integration state, so treat it as unavailable rather than crashing on
        // an undefined organization/profile when the user uploads an invoice early.
        const id = this.organization?.id;
        if (!id) {
            return false;
        }

        const { DemoOrganizationId, AdminTestGroupOrganizationId, production } = environment;

        return (this.organization.profile?.hasApprovedPayabliApplication ?? false) ||
                (production && [DemoOrganizationId, AdminTestGroupOrganizationId].includes(id));
    }

    /**
     * Whether the async org-profile load (getOrganizationProfile) is still in flight. The mail-check
     * gate (hasCompleteOrgAddress) reads the org profile, which starts unloaded, so until the load
     * settles it reports false even for an org that genuinely has a complete address. The template
     * uses this flag to show a neutral "checking…" state instead of the misleading "Organization
     * address required" warning during that window, so a valid org no longer flashes the mail-check
     * option as unavailable for the several seconds the profile GET takes to resolve (SD-7438).
     *
     * Scope note: this only drives the mail-check warning treatment. The Payabli-gated "Let the
     * Vendor Decide" option (hasPayabliIntegration) is still hidden — not shown as loading — during
     * the same window; that option has no false-warning problem, so it is intentionally left as-is.
     */
    get isOrganizationProfileLoading(): boolean {
        return !this.organizationProfileLoadSettled;
    }

    /**
     * Whether invoice OCR extraction should run for an uploaded invoice. Available when the org has an
     * approved Payabli bill-pay application (hasPayabliIntegration) OR the standalone
     * `invoice-ocr.enabled` feature flag is enabled for the org (SD-7794). The flag decouples invoice
     * OCR from Payabli payment so an org that does not pay via Payabli can still get extraction on
     * manual uploads — matching the already-ungated emailed-invoice path. The flag is unset for every
     * org unless a super-admin explicitly turns it on, so this is unchanged until an org opts in.
     */
    get isInvoiceOcrAvailable(): boolean {
        return this.hasPayabliIntegration ||
                LegFiJwtService.featureEnabled(CreatePayableDialogComponent.INVOICE_OCR_FEATURE_FLAG);
    }

    get invoiceActionInfoBannerText(): string {
        const selectedAction = this.payableActionFormGroup.get('invoiceAction').value;
        if (
                [PayableActionType.MARK_PAID, PayableActionType.SAVE_DRAFT].includes(selectedAction)
        ) {
            return 'This selection does not require approval.';
        } else {
            const remainingApprovals = this.localPayable?.currentFulfillment?.remainingApprovals;
            return `This payment requires ${remainingApprovals} approval` + (remainingApprovals > 1 ? 's' : '') +
                    (selectedAction === PayableActionType.SCHEDULE ? ' and will be processed if approved, based on the selected payment date.' : '.');
        }
    }

    get verifiedBillPayAccounts(): LobBankAccount[] {
        return this.bankAccounts.filter(account => account.verified);
    }

    get selectedVendorBankAccounts(): VendorBankAccount[] {
        return this.payableDetailForm?.selectedVendor?.vendorBankAccounts || [];
    }

    get isVendorAchEnabled(): boolean {
        return this.selectedVendorBankAccounts.length > 0;
    }

    get selectedAchBankAccount(): VendorBankAccount {
        const id = this.payableActionFormGroup.get('vendorBankAccountId')?.value;
        return this.selectedVendorBankAccounts?.find(a => a.id === id);
    }

    get hasTreasuryAccount(): boolean {
        return this._bankAccountInsights.hasTreasuryAccount(this.organization?.depositBankAccounts ?? []);
    }

    /** SD-10162: outbound transfers are held back only by second underwriting still under way. */
    get achAwaitsApproval(): boolean {
        return awaitsSecondUnderwriting(this.underwritingStatus, 'outboundTransfersEnabled');
    }

    /** Outbound transfers are off for a reason other than a review in progress. */
    get achSwitchedOff(): boolean {
        return this.underwritingStatus !== null
            && !this.underwritingStatus.outboundTransfersEnabled
            && !this.achAwaitsApproval;
    }

    /** SD-10162: the payable is more than the Treasury balance can pay. */
    get achExceedsBalance(): boolean {
        return this.treasuryAvailableCents !== null && (this.localPayable?.amount ?? 0) > this.treasuryAvailableCents;
    }

    trackByActionValue(_index: number, action: PayableAction): PayableActionType {
        return action.value;
    }

    trackByVendorBankAccountId(_index: number, account: VendorBankAccount): number {
        return account.id;
    }

    navigateToVendorSettings(event: MouseEvent): void {
        event.stopPropagation();
        event.preventDefault();
        const vendorId = this.payableDetailForm?.selectedVendor?.id;
        if (vendorId) {
            this.closed.emit(null);
            this._router.navigate(['/app/vendor/details', vendorId]);
        }
    }

    get formattedPaymentDateForReview(): string {
        const form = this.payableActionFormGroup;
        const invoiceAction = form.get('invoiceAction')?.value;
        const paymentMethod = form.get('paymentMethod')?.value;
        const whenToPay = form.get('whenToPay')?.value;

        if (invoiceAction === PayableActionType.MARK_PAID) {
            const date = this.markAsPaidFormComponent?.getPaymentDate();
            if (date) {
                return moment(date).format('YYYY-MM-DD');
            }
        }

        if (paymentMethod === PaymentMethod.PRINT_CHECK) {
            const date = form.get('checkDate')?.value;
            if (date) {
                return moment(date).format('YYYY-MM-DD');
            }
        }

        if (whenToPay === PaymentSchedule.NO_EARLIER_THAN) {
            const date = form.get('earliestPaymentDate')?.value;
            if (date) {
                return moment(date).format('YYYY-MM-DD');
            }
        }

        return '--';
    }

    /**
     * SD-9900: only orgs with a Treasury deposit account can select ACH, so only they can see the
     * gate — orgs without one never render the ACH option and the underwriting status can't affect
     * anything they see. `hasTreasuryAccount` reads the org profile, which loads async, so wait for
     * the load to settle (or decide immediately if it already has) and skip the request entirely
     * when no Treasury account is present. Mirrors the org-load deferral in
     * {@link handleInvoiceUploadedEvent}.
     */
    private loadUnderwritingStatusWhenTreasuryAccountPresent(): void {
        if (this.organizationProfileLoadSettled || this.organization) {
            if (this.hasTreasuryAccount) {
                this.loadUnderwritingStatus();
            }

            return;
        }

        this.organizationLoaded$
            .pipe(
                take(1),
                // Never wait forever: if the profile GET hangs, fall back to skipping the status
                // check rather than leaving the ACH option in an indeterminate gate.
                timeout(CreatePayableDialogComponent.ORG_PROFILE_SETTLE_TIMEOUT_MS),
                catchError(() => of(undefined)),
                untilDestroyed(this),
            )
            .subscribe(() => {
                if (this.hasTreasuryAccount) {
                    this.loadUnderwritingStatus();
                }
            });
    }

    /**
     * SD-9900 / SD-10162: read what the ACH option is gated on — the org's second-underwriting
     * status and the Treasury balance it pays from. A failed read of either gates nothing on it:
     * the backend refuses an ACH payment it cannot make.
     */
    private loadUnderwritingStatus(): void {
        this._treasuryService.getUnderwritingStatus(this.organizationId)
            .pipe(untilDestroyed(this))
            .subscribe((status) => {
                this.underwritingStatus = status;
                this._changeDetectorRef.detectChanges();
            });

        this._treasuryService.getBalance(this.organizationId)
            .pipe(
                catchError(() => of(null)),
                untilDestroyed(this),
            )
            .subscribe((response) => {
                this.treasuryAvailableCents = response?.balance?.available ?? null;
                this._changeDetectorRef.detectChanges();
            });
    }


    getOrganizationProfile() {
        const jwt = LegFiJwtService.read();
        const withs = ['profile'];
        if (jwt?.admin || jwt?.superUser) {
            withs.push('depositBankAccounts');
        }
        // Re-enter the loading state so a retry from the error banner shows the "checking…" spinner
        // again rather than staying on the failed state (SD-7438).
        this.organizationProfileLoadSettled = false;
        this.organizationProfileLoadFailed = false;
        this._organizationService.getOrganizationById(this.organizationId, withs)
            // Fail-safe: if the GET never emits and never errors, cap the wait so the "checking…"
            // spinner can't spin forever — the timeout routes to the error path below, which surfaces
            // the retry state and releases any upload waiting on the profile (SD-7438).
            .pipe(timeout(CreatePayableDialogComponent.ORG_PROFILE_SETTLE_TIMEOUT_MS), untilDestroyed(this))
            .subscribe({
            next: (organization) => {
                this.organization = organization;
                this.checkOrgAddressCompleteness();
                this.markOrganizationProfileSettled();
            },
            error: (err) => {
                this.growlerService.oops('Error checking organization profile');
                // The load failed (or timed out): record it so the template shows a distinct
                // "couldn't verify — retry" state instead of the misleading "address required" warning,
                // and still release any upload waiting on the profile so OCR falls back to manual entry
                // rather than hanging (SD-7626 / SD-7438).
                this.organizationProfileLoadFailed = true;
                this.markOrganizationProfileSettled();
            },
        });
    }

    /**
     * Mark the org-profile load as settled and release any upload waiting on it. Called from both the
     * success and error paths of {@link getOrganizationProfile} (SD-7626).
     */
    private markOrganizationProfileSettled(): void {
        this.organizationProfileLoadSettled = true;
        this.organizationLoaded$.next();
    }

    checkOrgAddressCompleteness(): void {
        const profile = this.organization?.profile;
        this.hasCompleteOrgAddress = !!(
            profile?.address1 &&
            profile?.city &&
            profile?.state &&
            profile?.zip
        );
    }

    openOrgAddressModal(): void {
        this._dialog.open(OrgAddressDialogComponent, { // kitlint-allow-material: OrgAddressDialog lives outside this folder and is still a legacy MatDialog
            width: MatDialogSizes.MD,
            data: { organization: this.organization },
        }).afterClosed().subscribe((result: GenericDialogResult) => {
            if (result?.success) {
                this.getOrganizationProfile();
            }
        });
    }

    addBankAccount() {
        this.addBankAccountOpen = true;
    }

    onAddBankAccountClosed(result: AddBillPayBankAccountOutput | null): void {
        this.addBankAccountOpen = false;
        if (result?.success) {
            this.getBankAccounts();
        }
    }

    getPotentialTransactionMatches() {
        this._payablesService.getPotentialTransactionMatches(this.localPayable).pipe(untilDestroyed(this)).subscribe({
            next: (matches: Transaction[]) => {
                this.potentialTransactionMatches = matches;
                if (this.markAsPaidFormComponent) {
                    this.markAsPaidFormComponent.handleTransactionMatchingState(matches);
                }
            },
            error: () => {
                this.growlerService.oops('There was a problem loading potential transaction matches.');
            },
        });
    }

    getBankAccounts() {
        this._bankAccountsService.getBillPayAccounts(this.organizationId).pipe(untilDestroyed(this)).subscribe({
            next: (accounts) => {
                this.bankAccounts = accounts;
            },
            error: (error) => {
                this.growlerService.oops('There was a problem loading bank accounts.');
            },
        });
    }

    handleInvoiceUploadingEvent() {
        this.isProcessing = true;
    }

    handleInvoiceUploadErrorEvent() {
        this.isProcessing = false;
    }

    /**
     * Handles a completed invoice upload. OCR availability is gated on isInvoiceOcrAvailable (Payabli
     * integration or the invoice-ocr feature flag), which depends on the async org-profile load
     * (getOrganizationProfile). A drag-dropped file can finish
     * uploading before that load resolves; reading the gate synchronously then would wrongly skip OCR
     * and leave the form blank (SD-7626). If the profile has already settled (loaded or errored) we
     * decide immediately; otherwise we wait for it to settle, then decide — so OCR is never silently
     * skipped just because the profile load had not arrived yet.
     */
    handleInvoiceUploadedEvent(uploadedFile: UploadedFile) {
        this.invoiceUploadedFile = uploadedFile;

        if (this.organizationProfileLoadSettled || this.organization) {
            this.dispatchOcrForUploadedFile(uploadedFile);
            return;
        }

        this.organizationLoaded$
            .pipe(
                take(1),
                // Never wait forever: if the profile GET hangs (no next, no error), fall back to the
                // skip/manual-entry decision so the stepper can't stay locked indefinitely (SD-7626).
                timeout(CreatePayableDialogComponent.ORG_PROFILE_SETTLE_TIMEOUT_MS),
                catchError(() => of(undefined)),
                untilDestroyed(this),
            )
            .subscribe(() => this.dispatchOcrForUploadedFile(uploadedFile));
    }

    /**
     * Processes an uploaded invoice with conditional OCR scanning, once the org profile is known.
     *
     * OCR availability is decided by {@link isInvoiceOcrAvailable}: the org has an approved Payabli
     * application (production check or explicit payabliId) OR the standalone `invoice-ocr.enabled`
     * feature flag is enabled for the org (SD-7794). OCR extracts vendor, amount, and due date from
     * the invoice to pre-populate the form fields. Split from {@link handleInvoiceUploadedEvent} so
     * the decision runs against a resolved org profile — immediately if already loaded, or deferred
     * until the load settles (SD-7626).
     */
    private dispatchOcrForUploadedFile(uploadedFile: UploadedFile) {
        if (!this.isInvoiceOcrAvailable) {
            this.isProcessing = false;
            return;
        }

        // OCR will actually run for this org — surface the extraction overlay until it settles (SD-7656).
        this.isExtractingInvoice = true;

        // The upload request dispatches OCR to the server queue and returns immediately (SD-7269).
        // We then resolve the result from whichever lands first: a live broadcast on the org's
        // private payable-ocr channel, or a polling fallback that reads the cached status on an
        // interval. The poll guarantees the stepper unlocks once OCR finishes even if the push is
        // missed, so a slow extraction can no longer leave the dialog frozen.
        const fileId = uploadedFile.id;
        const channelName = 'Organization-' + this.organizationId + '-payable-ocr';
        this._payabliService.getOcrResults(fileId)
            .pipe(
                switchMap((initial: {
                    ocrStatus: string,
                    validFileType: boolean,
                    payable: Payable,
                }) => {
                    if (initial.payable) {
                        this.localPayable = new Payable(initial.payable);
                    }
                    // Nothing to extract from a non-OCR file type -- stop here, upload is saved.
                    if (!initial.validFileType) {
                        return of({ ocrAttempted: false, ocrSuccess: false, payable: initial.payable });
                    }
                    return this.awaitOcrResult(channelName, fileId);
                }),
                timeout(CreatePayableDialogComponent.OCR_RESULT_TIMEOUT_MS),
                take(1),
                finalize(() => {
                    // Always release the channel and unlock the stepper, whatever the outcome
                    // (success, OCR failure, or the timeout cap) — clears the extraction overlay too.
                    this._echoService.leave(channelName);
                    this.isProcessing = false;
                    this.isExtractingInvoice = false;
                }),
                untilDestroyed(this),
            )
            .subscribe({
                next: (ocrResult: {
                    ocrAttempted: boolean,
                    ocrSuccess: boolean,
                    payable: Payable,
                }) => {
                    if (ocrResult.payable) {
                        this.localPayable = new Payable(ocrResult.payable);
                    }
                    if (ocrResult.ocrAttempted && !ocrResult.ocrSuccess) {
                        this.growlerService.warning(CreatePayableDialogComponent.OCR_FAILED_TITLE, CreatePayableDialogComponent.OCR_FAILED_BODY);
                    }
                },
                error: () => {
                    // OCR is best-effort: on failure or when the safety cap is hit, let the user enter
                    // details manually. finalize() clears isProcessing so the stepper is never locked.
                    this.growlerService.warning(CreatePayableDialogComponent.OCR_FAILED_TITLE, CreatePayableDialogComponent.OCR_FAILED_BODY);
                },
            });
    }

    /**
     * Resolves with the OCR result for a file as soon as it is available (SD-7269), from whichever
     * source produces a non-pending status first:
     *  - the live broadcast on the org's private payable-ocr channel (instant), or
     *  - a polling fallback that reads the cached status on an interval.
     * The channel is org-scoped, so the broadcast is matched on uploadedFileId and results for
     * other concurrent uploads are ignored. The poll makes the broadcast a best-effort
     * optimisation rather than a single point of failure: a missed push still resolves on the next
     * read, and the outer `timeout()` still caps the total wait.
     */
    private awaitOcrResult(channelName: string, fileId: number): Observable<{
        ocrAttempted: boolean,
        ocrSuccess: boolean,
        payable: Payable,
    }> {
        const push$ = new Observable<OcrStatusResult>((subscriber) => {
            this._echoService.private(channelName)
                .listen('.invoice.ocr.completed', (event: OcrStatusResult) => {
                    if (event.uploadedFileId === fileId) {
                        subscriber.next(event);
                    }
                });
        });

        const poll$ = timer(
            CreatePayableDialogComponent.OCR_POLL_INITIAL_DELAY_MS,
            CreatePayableDialogComponent.OCR_POLL_INTERVAL_MS,
        ).pipe(
            // exhaustMap so a slow status read is never overlapped by the next interval tick.
            exhaustMap(() => this._payabliService.getOcrStatus(fileId).pipe(
                // A transient error on a poll read must not drop the user to manual entry;
                // treat it as pending and let a later read (or the live push) decide.
                catchError(() => of({ ocrStatus: 'pending' })),
            ) as Observable<OcrStatusResult>),
        );

        return merge(push$, poll$).pipe(
            filter((result: OcrStatusResult) => result.ocrStatus !== 'pending'),
            take(1),
            map((result: OcrStatusResult) => ({
                ocrAttempted: !!result.ocrAttempted,
                ocrSuccess: !!result.ocrSuccess,
                payable: result.payable,
            })),
        );
    }

    discardInvoice() {
        if (!this.invoiceAttachment || this.isExtractingInvoice) {
            return EMPTY;
        }

        this.confirmRequest = {
            heading: 'Discard invoice',
            message: 'Are you sure you want to remove the uploaded invoice? This action cannot be undone.',
            confirmLabel: 'Discard invoice',
            destructive: true,
            onClose: (confirmed) => {
                if (confirmed) {
                    this.invoiceUploadedFile = null;
                    this.isProcessing = false;
                }
            },
        };
    }

    onStepPicked(event: Event) {
        this.goToStep((event as CustomEvent<number>).detail);
    }

    goToStep(next: number) {
        const previous = this.step;
        this.step = next;

        // Only reset validation state when moving backward
        if (next < previous) {
            this.payableActionFormGroup?.markAsUntouched();
            this.markAsPaidFormComponent?.markAsUntouched();
        }

        // If the user selects mark as paid, get potential transaction matches for the final step
        if (
                next === 2 &&
                this.payableActionFormGroup?.get('invoiceAction').value === PayableActionType.MARK_PAID
        ) {
            this.getPotentialTransactionMatches();
        }
    }

    handleFirstStep() {
        this.isProcessing = true;

        // Save current state before API call to detect auto-prepopulated memo
        const previousInvoiceNumber = this.localPayable?.invoiceNumber;
        const previousMemo = this.localPayable?.memo;

        this.payableDetailForm.previewPayable()
                .pipe(
                        take(1),
                        finalize(() => {
                            this.isProcessing = false;
                        }),
                )
                .subscribe({
                    next: (payable) => {
                        // The api doesn't return the id when hydrating the preview,
                        // so we set it manually here for it pass up in the final step and persist the changes
                        const id = this.localPayable?.id;
                        if (id) {
                            this.localPayable = payable;
                            this.localPayable.id = id;
                        } else {
                            this.localPayable = payable;
                        }

                        // If Print Check is selected and memo was auto-prepopulated (blank or matched old invoice number),
                        // update it with the new invoice number
                        if (this.payableActionFormGroup.get('paymentMethod')?.value === PaymentMethod.PRINT_CHECK) {
                            const memoWasAutoPrepopulated = !previousMemo || previousMemo === previousInvoiceNumber;
                            if (memoWasAutoPrepopulated && this.localPayable?.invoiceNumber) {
                                this.localPayable.memo = this.localPayable.invoiceNumber;
                            }
                        }

                        this.goToStep(1);
                    },
                    error: () => {
                        // Error handling is done in PayableDetailForm
                    },
                });
    }

    handleSecondStep() {
        // Mark all controls as touched to trigger validation messages
        Object.keys(this.payableActionFormGroup.controls).forEach(key => {
            const control = this.payableActionFormGroup.get(key);
            if (control.validator) { // Only mark controls with validators as touched
                control.markAsTouched();
                control.updateValueAndValidity();
            }
        });

        if (this.payableActionFormGroup.invalid) {
            return this.growlerService.oops('Please complete all required fields');
        }

        const schedulingAch =
                this.payableActionFormGroup.get('invoiceAction').value === PayableActionType.SCHEDULE &&
                this.payableActionFormGroup.get('paymentMethod').value === PaymentMethod.ACH_TRANSFER;

        // SD-10162 (F-f1f1): the amount stays editable, so ACH chosen within the balance survives going
        // Back and raising the amount; the disabled radio does not clear the selection. Re-check here.
        if (schedulingAch && this.achExceedsBalance) {
            return this.growlerService.oops(this.achExceedsBalanceMessage);
        }

        // SD-10162: ACH the balance covers, held back only by second underwriting still under way.
        if (schedulingAch && this.achAwaitsApproval) {
            this.approvalRequiredOpen = true;
            return;
        }

        this.goToStep(2);
    }

    handleFinalStep() {
        // Prevent duplicate submissions from rapid clicks
        if (this.isProcessing) {
            return;
        }

        if (
                this.payableActionFormGroup.get('invoiceAction').value === PayableActionType.MARK_PAID &&
                (!this.markAsPaidFormComponent || !this.markAsPaidFormComponent.isValid())
        ) {
            return this.growlerService.oops('Please complete all required fields');
        }

        // Different approach for Print Check payment method
        if (
                this.payableActionFormGroup.get('invoiceAction').value === PayableActionType.SCHEDULE &&
                this.payableActionFormGroup.get('paymentMethod').value === PaymentMethod.PRINT_CHECK
        ) {
            this.createCheckBatchAndPayable();
        } else if (
                this.payableActionFormGroup.get('invoiceAction').value === PayableActionType.SCHEDULE &&
                this.payableActionFormGroup.get('paymentMethod').value === PaymentMethod.ACH_TRANSFER
        ) {
            this.createPayableWithAchTransfer();
        } else {
            this.createPayableDirectly();
        }
    }

    closeDialog() {
        this.handleCloseAttempt();
        return EMPTY;
    }

    handleCloseAttempt(): void {
        if (this.step > 0 && !this.isClosing) {
            this.handleBackdropClick();
        } else {
            this.closed.emit(null);
        }
    }

    handleBackdropClick(): void {
        if (!this.isClosing) {
            this.isClosing = true;

            this.confirmRequest = {
                heading: 'Discard changes?',
                message: 'Are you sure you want to close? Unsaved changes will be lost.',
                confirmLabel: 'Yes, discard',
                cancelLabel: 'Continue editing',
                destructive: true,
                onClose: (confirmed) => {
                    if (confirmed) {
                        this.closed.emit(null);
                    }
                    this.isClosing = false;
                },
            };
        }
    }

    loadCheckPrintingDefaults(): void {
        this._checkPrintingSettingsService.getSettings()
            .pipe(take(1))
            .subscribe({
                next: (settings) => {
                    this.checkPrintingDefaults = {
                        format: settings.defaultCheckFormat || 'one-per-page',
                        displayBankAccount: settings.defaultDisplayBankAccount || false,
                        displayPayor: settings.defaultDisplayPayor || false,
                        displayCheckNumber: settings.defaultDisplayCheckNumber || false,
                        displayVendorAddress: settings.defaultDisplayVendorAddress || false
                    };
                },
            });
    }

    applyCheckPrintingDefaults(): void {
        if (this.checkFormatOptions && this.checkPrintingDefaults) {
            if (!this.checkFormatOptions.formatForm.dirty) {
                setTimeout(() => {
                    this.checkFormatOptions.setFormatValues(this.checkPrintingDefaults);
                    this._changeDetectorRef.detectChanges();
                }, 0);
            }
        }
    }

    /** Initialize Payable Action Form */
    private initPayableActionForm(): FormGroup {
        return new FormGroup ({
            invoiceAction: new FormControl<PayableActionType>(null, [Validators.required]),
            paymentMethod: new FormControl<PaymentMethod>(null, [Validators.required]),
            whenToPay: new FormControl<PaymentSchedule>(null),
            earliestPaymentDate: new FormControl<string>(null),
            bankAccountId: new FormControl<number>(null),
            checkNumber: new FormControl<string>(null),
            checkDate: new FormControl<string>(null),
            vendorBankAccountId: new FormControl<number>(null),
            checkStubMessage: new FormControl<string>(null, [Validators.maxLength(400)]),
        });
    }

    /**
     * Configures cascading validation based on action type and payment method.
     *
     * Validation dynamically adjusts: mail check requires bank account and scheduling,
     * print check needs bank account and check date, mark as paid has no payment method requirements.
     */
    private setupFormValidation(): void {
        this.setupInvoiceActionValidation();
        this.setupPaymentMethodValidation();
        this.setupWhenToPayValidation();
    }

    /** Payment Method Validation */
    private setupPaymentMethodValidation(): void {
        this.payableActionFormGroup.get('paymentMethod').valueChanges
            .pipe(
                distinctUntilChanged(),
                untilDestroyed(this)
            )
            .subscribe(method => {
                const bankAccountControl = this.payableActionFormGroup.get('bankAccountId');
                const checkDateControl = this.payableActionFormGroup.get('checkDate');
                const whenToPayControl = this.payableActionFormGroup.get('whenToPay');
                const checkNumberControl = this.payableActionFormGroup.get('checkNumber');
                const earliestPaymentDateControl = this.payableActionFormGroup.get('earliestPaymentDate');
                const vendorBankAccountControl = this.payableActionFormGroup.get('vendorBankAccountId');
                const checkStubMessageControl = this.payableActionFormGroup.get('checkStubMessage');

                // First clear all validators and reset values
                [bankAccountControl, checkDateControl, whenToPayControl, checkNumberControl, earliestPaymentDateControl, vendorBankAccountControl, checkStubMessageControl]
                    .forEach(control => {
                        if (control) {
                            control.clearValidators();
                            control.reset();
                            control.updateValueAndValidity();
                        }
                    });

                // Then set appropriate validators based on payment method
                switch (method) {
                    case PaymentMethod.MAIL_CHECK:
                        bankAccountControl.setValidators([Validators.required]);
                        checkNumberControl.setValidators([
                            Validators.required,
                            Validators.min(1),
                            Validators.max(500000000)
                        ]);
                        whenToPayControl.setValidators([Validators.required]);
                        checkStubMessageControl.setValidators([Validators.maxLength(400)]);
                        break;

                    case PaymentMethod.PRINT_CHECK:
                        // Prepopulate memo with invoice number if memo is blank
                        if (!this.localPayable?.memo && this.localPayable?.invoiceNumber) {
                            this.localPayable.memo = this.localPayable.invoiceNumber;
                        }
                        bankAccountControl.setValidators([Validators.required]);
                        checkDateControl.setValidators([Validators.required]);
                        checkNumberControl.setValidators([
                            Validators.required,
                            Validators.min(1),
                            Validators.max(500000000)
                        ]);
                        break;

                    case PaymentMethod.VENDOR_DECIDE:
                        whenToPayControl.setValidators([Validators.required]);
                        // No other validators needed
                        break;

                    case PaymentMethod.ACH_TRANSFER:
                        vendorBankAccountControl.setValidators([Validators.required]);
                        // Auto-select first bank account
                        if (this.selectedVendorBankAccounts?.length > 0) {
                            vendorBankAccountControl.setValue(this.selectedVendorBankAccounts[0].id);
                        }
                        // ACH supports scheduling for a future date (parity with mail check).
                        // setupWhenToPayValidation() drives the earliestPaymentDate requirement.
                        whenToPayControl.setValidators([Validators.required]);
                        break;

                    default:
                        // If no method selected, no validators needed
                        break;
                }

                // Update validity for all controls after setting new validators
                [bankAccountControl, checkDateControl, whenToPayControl, checkNumberControl, earliestPaymentDateControl, vendorBankAccountControl, checkStubMessageControl]
                    .forEach(control => {
                        if (control) {
                            control.updateValueAndValidity();
                        }
                    });
            });
    }

    /** When to Pay Validation */
    private setupWhenToPayValidation(): void {
        this.payableActionFormGroup.get('whenToPay').valueChanges
                .pipe(distinctUntilChanged(), untilDestroyed(this))
                .subscribe(whenToPay => {
                    const earliestPaymentDateControl = this.payableActionFormGroup.get('earliestPaymentDate');

                    if (whenToPay === PaymentSchedule.NO_EARLIER_THAN) {
                        earliestPaymentDateControl.setValidators([Validators.required]);
                    } else {
                        earliestPaymentDateControl.clearValidators();
                    }

                    earliestPaymentDateControl.updateValueAndValidity();
                });
    }

    /** Invoice Action Validation */
    private setupInvoiceActionValidation(): void {
        this.payableActionFormGroup.get('invoiceAction').valueChanges
            .pipe(
                distinctUntilChanged(),
                untilDestroyed(this)
            )
            .subscribe((action: PayableActionType) => {
                // Mark the control as touched when it changes
                this.payableActionFormGroup.get('invoiceAction').markAsTouched();

                // Reset payment method and when to pay controls
                this.payableActionFormGroup.get('paymentMethod').reset();
                this.payableActionFormGroup.get('whenToPay').reset();

                // Clear validators from payment method and when to pay
                this.payableActionFormGroup.get('paymentMethod').clearValidators();
                this.payableActionFormGroup.get('whenToPay').clearValidators();

                // Add validators back based on selected action
                if (action === PayableActionType.SCHEDULE) {
                    this.payableActionFormGroup.get('paymentMethod').setValidators([Validators.required]);
                    this.payableActionFormGroup.get('whenToPay').setValidators([Validators.required]);
                }

                // Update validity
                this.payableActionFormGroup.get('paymentMethod').updateValueAndValidity();
                this.payableActionFormGroup.get('whenToPay').updateValueAndValidity();
            });
    }

    /**
     * Creates check batch first, then payable with batch reference.
     *
     * Print check workflow requires batch creation before payable to generate PDF.
     * Without approval, immediately downloads check; with approval, check available post-approval.
     */
    private createCheckBatchAndPayable(): void {
        this.isProcessing = true;

        const bankAccountId = this.payableActionFormGroup.get('bankAccountId').value;
        const formatValues = this.checkFormatOptions?.getFormatValues() || this.checkPrintingDefaults;

        // Create the check data object in the same format as the check-printing dialog
        const checkData = {
            checks: [{
                payeeType: 'vendor',
                vendorId: this.localPayable.vendorId,
                payee: this.localPayable.vendor?.name || '',
                memo: this.localPayable.memo || '',
                amount: this.localPayable.amount,
                checkDate: this.payableActionFormGroup.get('checkDate').value,
                checkNumber: this.payableActionFormGroup.get('checkNumber').value,
                lobBankAccountId: bankAccountId
            }]
        };

        // Create the check batch first
        this._checkPrintingService.submitPrintedChecks(checkData, formatValues)
                .pipe(
                        switchMap((batch: PrintableCheckBatch) => {
                            const formattedRequest = {
                                organizationId: this.organizationId,
                                payable: {
                                    ...this.localPayable,
                                    dueDate: moment(this.localPayable.dueDate).format('YYYY-MM-DD'),
                                    invoiceDate: moment(this.localPayable.invoiceDate).format('YYYY-MM-DD'),
                                    invoiceUploadedFileId: this.invoiceUploadedFile?.id,
                                },
                                payableAction: {
                                    invoiceAction: PayableActionType.SCHEDULE,
                                    paymentMethod: PaymentMethod.PRINT_CHECK,
                                    checkDate: moment(this.payableActionFormGroup.get('checkDate').value).format('YYYY-MM-DD'),
                                    checkNumber: this.payableActionFormGroup.get('checkNumber').value,
                                    ...formatValues,
                                    earliestPaymentDate: null,
                                    printableCheckBatchId: batch.id,
                                }
                            };

                            // Create the payable with the batch reference
                            return this._payablesService.createPayable(formattedRequest);
                        }),
                        take(1),
                        finalize(() => {
                            this.isProcessing = false;
                        })
                )
                .subscribe({
                    next: (payable) => {
                        this.localPayable = payable;
                        this.closed.emit(<CreatePayableDialogResult>{success: true});

                        // If there is no approval rule, initiate download of the check file
                        if (!this.localPayable.currentFulfillment.approvalRule) {
                            this.growlerService.success(
                                    'Success',
                                    'Payable created successfully! Your check download will begin in a moment.'
                            );
                            this.downloadPrintedCheckFileAndNavigate(payable);
                        } else {
                            this.growlerService.success(
                                    'Success',
                                    'Payable created successfully! Your check will be available after approval.'
                            );
                            this.navigateToPayableDetailsIfApplicable(payable);
                        }
                    },
                    error: (err) => {
                        const jwt = LegFiJwtService.read();
                        let errorMessage = 'Error creating payable: ' + (err.error?.message || '');

                        if (jwt?.superUser) {
                            errorMessage += ' This action is required to be performed by a member of the org.';
                        }

                        this.growlerService.oops(errorMessage);
                    }
                });
    }

    private createPayableDirectly(): void {
        this.isProcessing = true;
        const formattedRequest = {
            organizationId: this.organizationId,
            payable: {
                ...this.localPayable,
                dueDate: moment(this.localPayable.dueDate).format('YYYY-MM-DD'),
                invoiceDate: moment(this.localPayable.invoiceDate).format('YYYY-MM-DD'),
                invoiceUploadedFileId: this.invoiceUploadedFile?.id,
            },
            payableAction: {
                ...this.payableActionFormGroup.value,
                earliestPaymentDate: this.payableActionFormGroup.get('earliestPaymentDate')?.value ?
                        moment(this.payableActionFormGroup.get('earliestPaymentDate')?.value).format('YYYY-MM-DD') : null,
                checkDate: this.payableActionFormGroup.get('checkDate')?.value ?
                        moment(this.payableActionFormGroup.get('checkDate')?.value).format('YYYY-MM-DD') : null,
            },
            markAsPaid: this.markAsPaidFormComponent && this.payableActionFormGroup.get('invoiceAction')?.value === PayableActionType.MARK_PAID ? {
                ...this.markAsPaidFormComponent.getFormGroup().getRawValue(),
                paymentDate: this.markAsPaidFormComponent.getFormGroup().get('paymentDate')?.value ?
                        moment(this.markAsPaidFormComponent.getFormGroup().get('paymentDate')?.value).format('YYYY-MM-DD') : null,
                bankAccountForTransaction: this.markAsPaidFormComponent.getFormGroup().get('bankAccountForTransaction')?.value?.id,
                existingTransactionToMatch: this.markAsPaidFormComponent.getFormGroup().get('existingTransactionToMatch')?.value,
            } : null,
        };

        this._payablesService.createPayable(formattedRequest)
            .pipe(
                take(1),
                finalize(() => {
                    this.isProcessing = false;
                }),
            )
            .subscribe({
                next: (payable) => {
                    this.localPayable = payable;
                    this.growlerService.success('Success', 'This payable has been created!');
                    this.closed.emit(<CreatePayableDialogResult>{success: true});
                    this.navigateToPayableDetailsIfApplicable(payable);
                },
                error: (err) => {
                    const jwt = LegFiJwtService.read();
                    let errorMessage = err.error?.message || 'There was a problem creating this payable.';

                    if (jwt?.superUser && !err.error?.message) {
                        errorMessage += ' This action is required to be performed by a member of the org.';
                    }

                    this.growlerService.oops(errorMessage);
                }
            });
    }


    private createPayableWithAchTransfer(): void {
        this.isProcessing = true;

        const formattedRequest = {
            organizationId: this.organizationId,
            payable: {
                ...this.localPayable,
                dueDate: moment(this.localPayable.dueDate).format('YYYY-MM-DD'),
                invoiceDate: moment(this.localPayable.invoiceDate).format('YYYY-MM-DD'),
                invoiceUploadedFileId: this.invoiceUploadedFile?.id,
            },
            payableAction: {
                ...this.payableActionFormGroup.value,
                // Send a formatted date only when scheduling for a future date; an
                // immediate ACH leaves this null and the backend sends right away.
                earliestPaymentDate: this.payableActionFormGroup.get('earliestPaymentDate')?.value ?
                        moment(this.payableActionFormGroup.get('earliestPaymentDate')?.value).format('YYYY-MM-DD') : null,
            },
        };

        // Backend handles outbound payment creation as part of payable fulfillment
        this._payablesService.createPayable(formattedRequest)
            .pipe(
                take(1),
                finalize(() => {
                    this.isProcessing = false;
                }),
            )
            .subscribe({
                next: (payable: Payable) => {
                    this.localPayable = payable;
                    const message = this.hasApprovalRule
                        ? 'Payable sent for approval.'
                        : 'Payable created and ACH transfer initiated!';
                    this.growlerService.success('Success', message);
                    this.closed.emit(<CreatePayableDialogResult>{success: true});
                    this.navigateToPayableDetailsIfApplicable(this.localPayable);
                },
                error: (err) => {
                    const errorMessage = err.error?.message || 'There was a problem creating the payable.';
                    this.growlerService.oops(errorMessage);
                },
            });
    }

    /**
     * Downloads the printed check file and then navigates to the payable details page.
     *
     * This ensures the backend has completed processing the check status update
     * (from "Approved" to "Check Printed") before loading the payable details,
     * preventing a race condition where stale data is displayed.
     */
    private downloadPrintedCheckFileAndNavigate(payable: Payable): void {
        this._payablesService.downloadPrintableCheckFile(this.localPayable)
            // SD-7271 / SD-7407 / SD-7474: the success path closes this dialog (destroying the
            // component) before the check-PDF response resolves. untilDestroyed(this) would cancel
            // the in-flight request, so the status flips to "Check Printed" but the PDF never
            // downloads. take(1) completes on the first emission independently of the component
            // lifecycle, so the single-shot download still fires after the dialog closes.
            .pipe(take(1))
            .subscribe({
            next: (response) => {
                this._csvExportService.downloadFile(
                        response,
                        'check.pdf',
                        'application/pdf'
                );
                // Navigate after payable status has been updated
                this.navigateToPayableDetailsIfApplicable(payable);
            },
            error: () => {
                this.growlerService.oops('Error downloading check');
                // Navigate anyway even if download fails
                this.navigateToPayableDetailsIfApplicable(payable);
            },
        });
    }

    private navigateToPayableDetailsIfApplicable(payable: Payable): void {
        if (this.data?.callerContext === PayableDialogCaller.FINANCIAL_ACCOUNTS) {
            return;
        }
        this._payableActionsService.viewPayableDetails(payable);
    }
}
